import { useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { useSelector } from 'hooks';
import { values } from 'lodash';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

import { Employee } from './EmployeeGrid/types';
import { OpenRequestsRow } from './OpenRequestsGrid/types';
import { Interval, PlanUnit } from 'components/types';

import { getRosterDays, prepareRosterForPrint } from '../utils';
import { getMonthInterval } from 'utils/date';
import { printContent } from 'utils/misc';

import Header from '../Header';
import EmployeeGrid from './EmployeeGrid/EmployeeGrid';
import HeaderFilters, { FilterOption, RefProps } from './HeaderFilters';
import OpenRequestsGrid from './OpenRequestsGrid/OpenRequestsGrid';
import RosterViewByEmployeeStyles from './RosterViewByEmployeeStyles';

type Props = {
  portal: 'employee' | 'roster';
  isAccordion?: boolean;
  getData: (
    interval: Interval,
    selectedPlanUnits: PlanUnit[]
  ) => Promise<{
    employees: Employee[];
    openRequestsRows: OpenRequestsRow[];
  }>;
  selectedPlanUnits: PlanUnit[];
  lastSelectedPlanUnitsIds?: (string | number)[] | null;
  onPlanUnitSelected: (selectedPlanUnits: PlanUnit[]) => void;
  showOnlyPlanned: boolean;
  onShowOnlyPlanned: () => void;
  defaultInterval?: Interval;
  onRosterViewChanged: (rosterView: 'employees' | 'duties') => void;
};

export default function RosterViewByEmployee(props: Props) {
  const {
    portal,
    isAccordion,
    getData,
    selectedPlanUnits,
    lastSelectedPlanUnitsIds,
    onPlanUnitSelected,
    showOnlyPlanned,
    onShowOnlyPlanned,
    defaultInterval,
    onRosterViewChanged
  } = props;
  const [interval, setInterval] = useState(
    defaultInterval || getMonthInterval()
  );

  const holidays = useSelector((state) => state.holidays);
  const [rosterDays, setRosterDays] = useState(
    getRosterDays(interval, holidays)
  );
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filterOption, setFilterOption] = useState<FilterOption | null>(null);
  const [openRequestsRows, setOpenRequestsRows] = useState<OpenRequestsRow[]>(
    []
  );
  const rosterRef = useRef(document.createElement('div'));
  const headerFiltersRef = useRef<RefProps>({} as RefProps);

  const onIntervalChanged = (interval: Interval) => {
    setInterval(interval);
    setRosterDays(getRosterDays(interval, holidays));
  };

  const employeesByPlanned = useMemo(
    () =>
      showOnlyPlanned
        ? employees.filter((e) => values(e.days).flat().length)
        : employees,
    [showOnlyPlanned, employees]
  );

  const filteredEmployees = useMemo(
    () =>
      employeesByPlanned.filter((e) => {
        if (filterOption) {
          if (filterOption.type === 'name') {
            return e.nameInformal === filterOption.label;
          }
          if (filterOption.type === 'function') {
            return e.benefit.function?.name === filterOption.label;
          }
          if (filterOption.type === 'planunit') {
            return (
              e.benefit.costCenter?.name === filterOption.label ||
              e.plannedCostCenter.name === filterOption.label
            );
          }
        }
        return true;
      }),
    [employeesByPlanned, filterOption]
  );

  useEffect(() => {
    setEmployees([]);
    setOpenRequestsRows([]);
    if (selectedPlanUnits.length) {
      getData(interval, selectedPlanUnits).then(
        ({ employees, openRequestsRows }) => {
          setEmployees(employees);
          setOpenRequestsRows(openRequestsRows);
        }
      );
    }
  }, [interval, selectedPlanUnits]);

  useEffect(() => {
    headerFiltersRef.current.clearSearchFilter();
  }, [selectedPlanUnits]);

  return (
    <RosterViewByEmployeeStyles
      className={classNames({ 'is-accordion': isAccordion })}
      ref={rosterRef}
    >
      <Header
        lastSelectedPlanUnitsIds={lastSelectedPlanUnitsIds}
        onPlanUnitSelected={onPlanUnitSelected}
        onPrintRoster={() => {
          printContent(prepareRosterForPrint(rosterRef.current));
        }}
        interval={interval}
        onIntervalChanged={onIntervalChanged}
        rosterDays={rosterDays}
        calendarView="month"
        rosterView="employees"
        onRosterViewChanged={onRosterViewChanged}
      >
        <HeaderFilters
          employees={employeesByPlanned}
          showOnlyPlanned={showOnlyPlanned}
          onShowOnlyPlanned={onShowOnlyPlanned}
          onFilterOptionSelected={setFilterOption}
          ref={headerFiltersRef}
        />
      </Header>
      <div className="roster-body">
        <OverlayScrollbarsComponent
          className="wrap-container"
          options={{
            paddingAbsolute: true
          }}
        >
          <EmployeeGrid
            employees={filteredEmployees}
            isAccordion={isAccordion}
            selectedPlanUnits={selectedPlanUnits}
            rosterDays={rosterDays}
            portal={portal}
            interval={interval}
          />
          <OpenRequestsGrid
            openRequestsRows={openRequestsRows}
            isAccordion={isAccordion}
            selectedPlanUnits={selectedPlanUnits}
            rosterDays={rosterDays}
            portal={portal}
          />
        </OverlayScrollbarsComponent>
      </div>
    </RosterViewByEmployeeStyles>
  );
}
